import { Box, BoxProps, Flex, HStack, Text, SkeletonCircle, SkeletonText } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { useMentionedUnderlyingsQuery } from "src/api/client/mentioned-underlyings.api";
import { ArrowLink } from "src/components/base/arrow-link";
import { WfHeading } from "src/components/base/wf-heading";
import { WfLinkGhost, WfLinkUnstyled } from "src/components/base/wf-link";
import { UnderlyingFollowButton } from "src/components/common/buttons/underlying-follow-button/underlying-follow-button";
import { EnumRankingType } from "src/components/common/ranking/types";
import { usePopulateUnderlyingFollowStore } from "src/hooks/following/use-populate-underlying-follow-store";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { IMentionedUnderlying } from "src/types/common";
import { RankingValue } from "../../common/ranking/ranking-value";
import { UnderlyingLogo } from "../underlying-overview/underlying-logo";

const LoadingMentionedUnderlying = (props: BoxProps) => {
    return (
        <Box p={2} bg="white" boxShadow="sm" borderRadius="xl" border="1px" borderColor="gray.100" {...props}>
            <Flex gap="1" flexGrow="1" minW="0">
                <SkeletonCircle size={["4", "8"]} />
                <Flex flexDir="column" flexGrow="1" justifyContent="center" minW={0}>
                    <SkeletonText w="200px" skeletonHeight="2" noOfLines={[3, 2]} />
                </Flex>
            </Flex>
        </Box>
    );
};

interface IMentionedUnderlyingProps extends BoxProps {
    underlying: IMentionedUnderlying;
}

const NEWS_MENTIONED_UNDERLYING_IN_WIKIFOLIOS_LINK_TEST_ID = "news-mentioned-underlying-in-wikifolios-link";
const NEWS_MENTIONED_UNDERLYING_LINK_TEST_ID = "news-mentioned-underlying-link";
const NEWS_MENTIONED_UNDERLYING_TEST_ID = "news-mentioned-underlying";

const MentionedUnderlying = (props: IMentionedUnderlyingProps) => {
    const { t } = useTranslation("common");
    const { buildUnderlyingLink, buildSearchLink } = useLinkBuilder();
    const { formatNumber } = useNumberFormatter();
    const { underlying, ...boxProps } = props;
    const { isin, canBeFollowed, containedInWikifolios, shortDescription, performance } = underlying;

    const underlyingLink = buildUnderlyingLink(isin);
    const searchLink = buildSearchLink({ searchTerm: isin, investable: false });
    const underlyingInWikifolios = !!containedInWikifolios && containedInWikifolios > 0;
    const searchWikifoliosLinkText =
        containedInWikifolios &&
        (containedInWikifolios === 1
            ? t("in-1-wikifolio", { 0: formatNumber(containedInWikifolios) })
            : t("in-x-wikifolios", { 0: formatNumber(containedInWikifolios) }));

    return (
        <Flex
            {...boxProps}
            p={2}
            bg="white"
            boxShadow="sm"
            borderRadius="xl"
            border="1px"
            borderColor="gray.100"
            pos="relative"
            gap={[1, 4]}
            data-test-id={NEWS_MENTIONED_UNDERLYING_TEST_ID}
        >
            <Flex gap="1" flexGrow="1" minW="0">
                <WfLinkUnstyled href={underlyingLink} display="inline-flex" alignItems={["flex-start", "center", "center"]}>
                    <UnderlyingLogo isin={isin} h={["32px", "72px"]} w={["32px", "72px"]} />
                </WfLinkUnstyled>
                <Flex flexDir="column" flexGrow="1" justifyContent="center" minW={0}>
                    <WfLinkGhost
                        href={underlyingLink}
                        size="lg"
                        fontWeight="semibold"
                        alignSelf="start"
                        maxW="100%"
                        noOfLines={1}
                        wordBreak="break-all"
                        data-test-id={NEWS_MENTIONED_UNDERLYING_LINK_TEST_ID}
                    >
                        {shortDescription}
                    </WfLinkGhost>
                    <HStack>
                        <RankingValue
                            size="xs"
                            fontWeight="bold"
                            data={{
                                label: "",
                                type: EnumRankingType.Performance,
                                value: performance,
                            }}
                        />
                        <Text fontSize="sm" color="gray.500" textTransform="lowercase">
                            {t("today")}
                        </Text>
                    </HStack>
                    {underlyingInWikifolios && (
                        <ArrowLink
                            display={["block", "none"]}
                            href={searchLink}
                            fontSize="sm"
                            data-test-id={NEWS_MENTIONED_UNDERLYING_IN_WIKIFOLIOS_LINK_TEST_ID}
                        >
                            {searchWikifoliosLinkText}
                        </ArrowLink>
                    )}
                </Flex>
            </Flex>
            <Box
                textAlign="right"
                display={[`${canBeFollowed ? "flex" : "none"}`, `${canBeFollowed || underlyingInWikifolios ? "block" : "none"}`]}
                verticalAlign="top"
            >
                {canBeFollowed && <UnderlyingFollowButton underlyingIsin={isin} iconOnlyBreakpoints={[true, false]} />}
                {underlyingInWikifolios && (
                    <ArrowLink
                        display={["none", "block"]}
                        href={searchLink}
                        fontSize="sm"
                        whiteSpace="nowrap"
                        mt={2}
                        data-test-id={NEWS_MENTIONED_UNDERLYING_IN_WIKIFOLIOS_LINK_TEST_ID}
                    >
                        {searchWikifoliosLinkText}
                    </ArrowLink>
                )}
            </Box>
        </Flex>
    );
};

interface INewsUnderlyingSectionProps extends BoxProps {
    isins: string[];
}

export const NewsUnderlyingSection = (props: INewsUnderlyingSectionProps) => {
    const { t } = useTranslation(["news"]);
    const { isins, ...boxProps } = props;

    const { data, isLoading, isError } = useMentionedUnderlyingsQuery({
        underlyingIsins: isins,
    });

    usePopulateUnderlyingFollowStore(data);

    if (isError) {
        return null;
    }

    if (isLoading || data?.length) {
        return (
            <Box as="section" {...boxProps}>
                <WfHeading as="h2" fontSize="2xl" mb="2">
                    {t("headline.stocks")}
                </WfHeading>
                {isLoading && (
                    <Box mb={[1, 2]}>
                        {isins.map(isin => (
                            <LoadingMentionedUnderlying key={isin} mb={[1, 2]} />
                        ))}
                    </Box>
                )}
                {data?.map(underlying => (
                    <MentionedUnderlying key={underlying.isin} mb={[1, 2]} underlying={underlying} />
                ))}
            </Box>
        );
    }

    return null;
};
